import React, {useCallback, useEffect, useState} from "react";
import {Button, Col, Input, InputNumber, message, Row, Space, Table, Tabs, Typography} from "antd";
import {ClearOutlined, FileExcelOutlined, FilePdfOutlined, SearchOutlined} from "@ant-design/icons";
import * as XLSX from "xlsx";
import {saveAs} from "file-saver";
import {jsPDF} from "jspdf";
import autoTable from "jspdf-autotable";
import attendanceReportService from "../services/AttendanceReportService";
import theme from "../theme";

const {Title} = Typography;

const pct = (v) => v != null ? `${v}%` : "0%";
const num = (v) => v ?? 0;

// Pasteles derivados del primary #9f1717, patrón alternado
const C = {
    a:       '#F9E5E5', // guinda muy claro (primary al 10%)
    b:       '#EDBBBB', // guinda claro (primary al 25%)
    borderA: '3px solid #D99090',
    borderB: '3px solid #C06060',
};

const col = (bg, borderLeft, extra = {}) => ({
    onHeaderCell: () => ({ style: { backgroundColor: bg, borderLeft } }),
    onCell:       () => ({ style: { backgroundColor: bg, borderLeft } }),
    align: "center",
    ...extra,
});

const COLUMNS_USER = [
    {title: "Usuario",                  dataIndex: "nombreCompleto",     key: "nombreCompleto",     ...col(C.a, C.borderA)},
    {title: "Rol",                      dataIndex: "rol",               key: "rol",               ...col(C.a, 'none')},
    {title: "Distrito Local",           dataIndex: "distritoLocal",     key: "distritoLocal",     ...col(C.a, 'none'), render: (v) => v ?? "—"},
    {title: "Cantidad de Pases de Lista", dataIndex: "cantidadPasesLista", key: "cantidadPasesLista", ...col(C.b, C.borderB), render: num},
];

// Patrón: A=guinda claro, B=guinda más oscuro, repite A-B-A-B por grupo
const COLUMNS_SECTION = [
    {title: "Distrito Local",             dataIndex: "distritoLocal",         key: "distritoLocal",         ...col(C.a, C.borderA)},
    {title: "Sección",                    dataIndex: "seccion",               key: "seccion",               ...col(C.a, 'none')},
    {title: "Total convencidos",          dataIndex: "totalConvencidos",      key: "totalConvencidos",      ...col(C.b, C.borderB), render: num},
    {title: "Asistencia",                 dataIndex: "asistenciaConvencidos", key: "asistenciaConvencidos", ...col(C.b, 'none'),    render: num},
    {title: "% asistencia",               dataIndex: "pctConvencidos",        key: "pctConvencidos",        ...col(C.b, 'none'),    render: pct},
    {title: "Afiliados (no convencidos)", dataIndex: "totalAfiliados",        key: "totalAfiliados",        ...col(C.a, C.borderA), render: num},
    {title: "Asistencia afiliados",       dataIndex: "asistenciaAfiliados",   key: "asistenciaAfiliados",   ...col(C.a, 'none'),    render: num},
    {title: "% asistencia",               dataIndex: "pctAfiliados",          key: "pctAfiliados",          ...col(C.a, 'none'),    render: pct},
    {title: "Promoción total",            dataIndex: "promocionTotal",        key: "promocionTotal",        ...col(C.b, C.borderB), render: num},
    {title: "Total asistencia",           dataIndex: "totalAsistencia",       key: "totalAsistencia",       ...col(C.b, 'none'),    render: num},
    {title: "% asistencia total",         dataIndex: "pctTotal",              key: "pctTotal",              ...col(C.b, 'none'),    render: pct},
];

const COLUMNS_DISTRICT = [
    {title: "Distrito Local",             dataIndex: "distritoLocal",         key: "distritoLocal",         ...col(C.a, C.borderA)},
    {title: "Total convencidos",          dataIndex: "totalConvencidos",      key: "totalConvencidos",      ...col(C.b, C.borderB), render: num},
    {title: "Asistencia",                 dataIndex: "asistenciaConvencidos", key: "asistenciaConvencidos", ...col(C.b, 'none'),    render: num},
    {title: "% asistencia",               dataIndex: "pctConvencidos",        key: "pctConvencidos",        ...col(C.b, 'none'),    render: pct},
    {title: "Afiliados (no convencidos)", dataIndex: "totalAfiliados",        key: "totalAfiliados",        ...col(C.a, C.borderA), render: num},
    {title: "Asistencia afiliados",       dataIndex: "asistenciaAfiliados",   key: "asistenciaAfiliados",   ...col(C.a, 'none'),    render: num},
    {title: "% asistencia",               dataIndex: "pctAfiliados",          key: "pctAfiliados",          ...col(C.a, 'none'),    render: pct},
    {title: "Promoción total",            dataIndex: "promocionTotal",        key: "promocionTotal",        ...col(C.b, C.borderB), render: num},
    {title: "Total asistencia",           dataIndex: "totalAsistencia",       key: "totalAsistencia",       ...col(C.b, 'none'),    render: num},
    {title: "% asistencia total",         dataIndex: "pctTotal",              key: "pctTotal",              ...col(C.b, 'none'),    render: pct},
];

const COLUMNS_ENTITY = [
    {title: "Total convencidos",          dataIndex: "totalConvencidos",      key: "totalConvencidos",      ...col(C.b, C.borderB), render: num},
    {title: "Asistencia",                 dataIndex: "asistenciaConvencidos", key: "asistenciaConvencidos", ...col(C.b, 'none'),    render: num},
    {title: "% asistencia",               dataIndex: "pctConvencidos",        key: "pctConvencidos",        ...col(C.b, 'none'),    render: pct},
    {title: "Afiliados (no convencidos)", dataIndex: "totalAfiliados",        key: "totalAfiliados",        ...col(C.a, C.borderA), render: num},
    {title: "Asistencia afiliados",       dataIndex: "asistenciaAfiliados",   key: "asistenciaAfiliados",   ...col(C.a, 'none'),    render: num},
    {title: "% asistencia",               dataIndex: "pctAfiliados",          key: "pctAfiliados",          ...col(C.a, 'none'),    render: pct},
    {title: "Promoción total",            dataIndex: "promocionTotal",        key: "promocionTotal",        ...col(C.b, C.borderB), render: num},
    {title: "Total asistencia",           dataIndex: "totalAsistencia",       key: "totalAsistencia",       ...col(C.b, 'none'),    render: num},
    {title: "% asistencia total",         dataIndex: "pctTotal",              key: "pctTotal",              ...col(C.b, 'none'),    render: pct},
];

const buildPDF = (title, columns, data) => {
    const doc = new jsPDF({orientation: "landscape"});
    const pageWidth = doc.internal.pageSize.getWidth();
    const now = new Date();

    doc.setFontSize(14);
    doc.setTextColor(80, 80, 80);
    doc.text(`Avance Asistencia — ${title}`, pageWidth / 2, 16, {align: "center"});
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.text(`Generado: ${now.toLocaleString("es-MX", {dateStyle: "long", timeStyle: "short"})}`, pageWidth / 2, 23, {align: "center"});

    autoTable(doc, {
        head: [columns.map(c => c.title)],
        body: data.map(row => columns.map(c => {
            const v = row[c.dataIndex];
            if (c.render === pct) return pct(v);
            return num(v);
        })),
        startY: 28,
        headStyles: {fillColor: [100, 100, 100], textColor: 255, fontStyle: "bold"},
        alternateRowStyles: {fillColor: [240, 240, 240]},
        styles: {fontSize: 7, cellPadding: 2, halign: "center"},
        didDrawPage: (d) => {
            if (d.pageNumber > 1) {
                doc.setFontSize(9);
                doc.setTextColor(120, 120, 120);
                doc.text(`Avance Asistencia — ${title}`, pageWidth / 2, 10, {align: "center"});
            }
        },
    });

    doc.save(`avance_asistencia_${title.toLowerCase().replace(/ /g, "_")}_${now.toISOString().slice(0, 10)}.pdf`);
};

const buildExcel = (title, columns, data) => {
    const rows = data.map(row => {
        const obj = {};
        columns.forEach(c => {
            const v = row[c.dataIndex];
            obj[c.title] = c.render === pct ? pct(v) : num(v);
        });
        return obj;
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, title.slice(0, 31));
    const now = new Date();
    const buf = XLSX.write(wb, {bookType: "xlsx", type: "array"});
    saveAs(new Blob([buf], {type: "application/octet-stream"}),
        `avance_asistencia_${title.toLowerCase().replace(/ /g, "_")}_${now.toISOString().slice(0, 10)}.xlsx`);
};

const AvanceAsistenciaPage = () => {
    // ── Tab 1: Por Sección ──────────────────────────────────────────────────
    const [seccionData, setSeccionData] = useState([]);
    const [seccionLoading, setSeccionLoading] = useState(false);
    const [seccionPagination, setSeccionPagination] = useState({current: 1, pageSize: 20, total: 0});
    const [seccionFilters, setSeccionFilters] = useState({distritoLocal: null, seccion: null});
    const [seccionApplied, setSeccionApplied] = useState({});

    // ── Tab 2: Por Distrito ─────────────────────────────────────────────────
    const [distritoData, setDistritoData] = useState([]);
    const [distritoLoading, setDistritoLoading] = useState(false);
    const [distritoFilter, setDistritoFilter] = useState(null);
    const [distritoApplied, setDistritoApplied] = useState({});

    // ── Tab 3: Por Entidad ──────────────────────────────────────────────────
    const [entidadData, setEntidadData] = useState([]);
    const [entidadLoading, setEntidadLoading] = useState(false);

    // ── Tab 4: Por Usuario ──────────────────────────────────────────────────
    const [usuarioData, setUsuarioData] = useState([]);
    const [usuarioLoading, setUsuarioLoading] = useState(false);
    const [usuarioFilters, setUsuarioFilters] = useState({distritoLocal: null, usuario: "", rol: ""});
    const [usuarioApplied, setUsuarioApplied] = useState({});

    const fetchSeccion = useCallback(async (page, pageSize, filters = {}) => {
        setSeccionLoading(true);
        try {
            const params = {page, pageSize, ...filters};
            const res = await attendanceReportService.getBySection(params);
            setSeccionData(res.data || []);
            setSeccionPagination(prev => ({...prev, current: page, pageSize, total: res.meta?.totalItems || 0}));
        } catch {
            message.error("Error al cargar reporte por sección");
        } finally {
            setSeccionLoading(false);
        }
    }, []);

    const fetchDistrito = useCallback(async (filters = {}) => {
        setDistritoLoading(true);
        try {
            const res = await attendanceReportService.getByDistrict(filters);
            setDistritoData(res.data || []);
        } catch {
            message.error("Error al cargar reporte por distrito");
        } finally {
            setDistritoLoading(false);
        }
    }, []);

    const fetchEntidad = useCallback(async () => {
        setEntidadLoading(true);
        try {
            const res = await attendanceReportService.getByEntity();
            setEntidadData(res.data ? [res.data] : []);
        } catch {
            message.error("Error al cargar reporte por entidad");
        } finally {
            setEntidadLoading(false);
        }
    }, []);

    const fetchUsuario = useCallback(async (filters = {}) => {
        setUsuarioLoading(true);
        try {
            const res = await attendanceReportService.getByUser(filters);
            setUsuarioData(res.data || []);
        } catch {
            message.error("Error al cargar reporte por usuario");
        } finally {
            setUsuarioLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSeccion(1, 20, {});
        fetchDistrito({});
        fetchEntidad();
        fetchUsuario({});
    }, [fetchSeccion, fetchDistrito, fetchEntidad, fetchUsuario]);

    const handleSeccionSearch = () => {
        const filters = {};
        if (seccionFilters.distritoLocal) filters.distritoLocal = seccionFilters.distritoLocal;
        if (seccionFilters.seccion) filters.seccion = seccionFilters.seccion;
        setSeccionApplied(filters);
        fetchSeccion(1, seccionPagination.pageSize, filters);
    };

    const handleSeccionReset = () => {
        setSeccionFilters({distritoLocal: null, seccion: null});
        setSeccionApplied({});
        fetchSeccion(1, seccionPagination.pageSize, {});
    };

    const exportPDF = (tab) => {
        if (tab === 1) buildPDF("Por Sección", COLUMNS_SECTION, seccionData);
        else if (tab === 2) buildPDF("Por Distrito", COLUMNS_DISTRICT, distritoData);
        else buildPDF("Por Entidad", COLUMNS_ENTITY, entidadData);
    };

    const exportExcel = (tab) => {
        if (tab === 1) buildExcel("Por Sección", COLUMNS_SECTION, seccionData);
        else if (tab === 2) buildExcel("Por Distrito", COLUMNS_DISTRICT, distritoData);
        else buildExcel("Por Entidad", COLUMNS_ENTITY, entidadData);
    };

    const exportButtons = (tab) => (
        <Space>
            <Button icon={<FilePdfOutlined/>} danger onClick={() => exportPDF(tab)}>PDF</Button>
            <Button icon={<FileExcelOutlined/>} style={{color: "#217346", borderColor: "#217346"}} onClick={() => exportExcel(tab)}>Excel</Button>
        </Space>
    );

    const items = [
        {
            key: "1",
            label: "Por Sección",
            children: (
                <>
                    <Row gutter={[12, 12]} style={{marginBottom: 16}}>
                        <Col xs={24} sm={8} md={6}>
                            <InputNumber
                                placeholder="Distrito Local"
                                value={seccionFilters.distritoLocal}
                                onChange={(v) => setSeccionFilters(f => ({...f, distritoLocal: v}))}
                                style={{width: "100%"}}
                                min={1}
                            />
                        </Col>
                        <Col xs={24} sm={8} md={6}>
                            <InputNumber
                                placeholder="Sección"
                                value={seccionFilters.seccion}
                                onChange={(v) => setSeccionFilters(f => ({...f, seccion: v}))}
                                style={{width: "100%"}}
                                min={1}
                            />
                        </Col>
                        <Col xs={24} sm={8} md={12}>
                            <Space wrap style={{justifyContent: "space-between", width: "100%"}}>
                                <Space>
                                    <Button type="primary" icon={<SearchOutlined/>} onClick={handleSeccionSearch}>
                                        Buscar
                                    </Button>
                                    <Button icon={<ClearOutlined/>} onClick={handleSeccionReset}>
                                        Restablecer
                                    </Button>
                                </Space>
                                {exportButtons(1)}
                            </Space>
                        </Col>
                    </Row>
                    <Table
                        columns={COLUMNS_SECTION}
                        dataSource={seccionData}
                        rowKey={(r) => `${r.distritoLocal}-${r.seccion}`}
                        loading={seccionLoading}
                        scroll={{x: "max-content"}}
                        size="small"
                        pagination={{
                            current: seccionPagination.current,
                            pageSize: seccionPagination.pageSize,
                            total: seccionPagination.total,
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "20", "50", "100"],
                            showTotal: (t) => `Total ${t} secciones`,
                            onChange: (page, pageSize) => fetchSeccion(page, pageSize, seccionApplied),
                        }}
                    />
                </>
            ),
        },
        {
            key: "2",
            label: "Por Distrito",
            children: (
                <>
                    <Row gutter={[12, 12]} style={{marginBottom: 16}}>
                        <Col xs={24} sm={8} md={6}>
                            <InputNumber
                                placeholder="Distrito Local"
                                value={distritoFilter}
                                onChange={(v) => setDistritoFilter(v)}
                                style={{width: "100%"}}
                                min={1}
                            />
                        </Col>
                        <Col xs={24} sm={16} md={18}>
                            <Space wrap style={{justifyContent: "space-between", width: "100%"}}>
                                <Space>
                                    <Button type="primary" icon={<SearchOutlined/>} onClick={() => {
                                        const filters = distritoFilter ? {distritoLocal: distritoFilter} : {};
                                        setDistritoApplied(filters);
                                        fetchDistrito(filters);
                                    }}>
                                        Buscar
                                    </Button>
                                    <Button icon={<ClearOutlined/>} onClick={() => {
                                        setDistritoFilter(null);
                                        setDistritoApplied({});
                                        fetchDistrito({});
                                    }}>
                                        Restablecer
                                    </Button>
                                </Space>
                                {exportButtons(2)}
                            </Space>
                        </Col>
                    </Row>
                    <Table
                        columns={COLUMNS_DISTRICT}
                        dataSource={distritoData}
                        rowKey="distritoLocal"
                        loading={distritoLoading}
                        scroll={{x: "max-content"}}
                        size="small"
                        pagination={false}
                    />
                </>
            ),
        },
        {
            key: "3",
            label: "Por Entidad",
            children: (
                <>
                    <div style={{display: "flex", justifyContent: "flex-end", marginBottom: 16}}>
                        {exportButtons(3)}
                    </div>
                    <Table
                        columns={COLUMNS_ENTITY}
                        dataSource={entidadData}
                        rowKey={(_, i) => i}
                        loading={entidadLoading}
                        scroll={{x: "max-content"}}
                        size="small"
                        pagination={false}
                    />
                </>
            ),
        },
        {
            key: "4",
            label: "Por Usuario",
            children: (
                <>
                    <Row gutter={[12, 12]} style={{marginBottom: 16}}>
                        <Col xs={24} sm={6} md={5}>
                            <InputNumber
                                placeholder="Distrito Local"
                                value={usuarioFilters.distritoLocal}
                                onChange={(v) => setUsuarioFilters(f => ({...f, distritoLocal: v}))}
                                style={{width: "100%"}}
                                min={1}
                            />
                        </Col>
                        <Col xs={24} sm={9} md={7}>
                            <Input
                                placeholder="Usuario o nombre"
                                value={usuarioFilters.usuario}
                                onChange={(e) => setUsuarioFilters(f => ({...f, usuario: e.target.value}))}
                            />
                        </Col>
                        <Col xs={24} sm={9} md={6}>
                            <Input
                                placeholder="Rol (ej. distrital)"
                                value={usuarioFilters.rol}
                                onChange={(e) => setUsuarioFilters(f => ({...f, rol: e.target.value}))}
                            />
                        </Col>
                        <Col xs={24} md={6}>
                            <Space wrap style={{justifyContent: "space-between", width: "100%"}}>
                                <Space>
                                    <Button type="primary" icon={<SearchOutlined/>} onClick={() => {
                                        const filters = {};
                                        if (usuarioFilters.distritoLocal) filters.distritoLocal = usuarioFilters.distritoLocal;
                                        if (usuarioFilters.usuario) filters.usuario = usuarioFilters.usuario;
                                        if (usuarioFilters.rol) filters.rol = usuarioFilters.rol;
                                        setUsuarioApplied(filters);
                                        fetchUsuario(filters);
                                    }}>
                                        Buscar
                                    </Button>
                                    <Button icon={<ClearOutlined/>} onClick={() => {
                                        setUsuarioFilters({distritoLocal: null, usuario: "", rol: ""});
                                        setUsuarioApplied({});
                                        fetchUsuario({});
                                    }}>
                                        Restablecer
                                    </Button>
                                </Space>
                                <Space>
                                    <Button icon={<FilePdfOutlined/>} danger onClick={() => buildPDF("Por Usuario", COLUMNS_USER, usuarioData)}>PDF</Button>
                                    <Button icon={<FileExcelOutlined/>} style={{color: "#217346", borderColor: "#217346"}} onClick={() => buildExcel("Por Usuario", COLUMNS_USER, usuarioData)}>Excel</Button>
                                </Space>
                            </Space>
                        </Col>
                    </Row>
                    <Table
                        columns={COLUMNS_USER}
                        dataSource={usuarioData}
                        rowKey="usuario"
                        loading={usuarioLoading}
                        scroll={{x: "max-content"}}
                        size="small"
                        pagination={{
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "20", "50"],
                            showTotal: (t) => `Total ${t} usuarios`,
                        }}
                    />
                </>
            ),
        },
    ];

    return (
        <div style={{
            maxWidth: "95%",
            width: "95vw",
            margin: "20px auto",
            background: theme.colors.card,
            padding: "20px",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        }}>
            <Title level={2} style={{textAlign: "center", marginBottom: 24}}>
                Avance Asistencia
            </Title>
            <Tabs defaultActiveKey="1" items={items}/>
        </div>
    );
};

export default AvanceAsistenciaPage;
